/**
 * Sheet tabs along the bottom, like a workbook: one sheet per open screen
 * (`model.openScreens`), then one DRAFT sheet per open editor (a + or pencil
 * mark, the record's title in italics, the unsaved dot), and a ⊕ that opens
 * the Menu panel to open another screen.
 *
 * A WAI-ARIA tablist: Left / Right / Home / End move between sheets, Enter /
 * Space switch, Delete closes the focused sheet (× does the same with the
 * mouse; an editor's close applies its own Save / Discard rule).
 */
import { CirclePlus, Pencil, Plus, X } from 'lucide-react'
import { useState, type KeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../../nav/navIcons'
import { UnsavedDot, editorTitle } from '../../nav/parts/EditorsList'
import { S, iconButton } from '../../nav/tokens'
import type { RunNavItem } from '../../nav/types'
import type { EditorSession, ShellNavModel } from '../../nav/useShellNav'
import { IconTip, moveFocus } from './shared'

type Sheet =
  | { key: string; kind: 'screen'; item: RunNavItem; selected: boolean }
  | { key: string; kind: 'editor'; session: EditorSession; selected: boolean }

export function SheetTabs({ model, controls, onNew }: {
  model: ShellNavModel
  /** The id of the content the sheets switch (aria-controls). */
  controls: string
  onNew: (from: HTMLElement) => void
}) {
  const current = model.current
  // The editor on display, if any: its draft sheet is the selected one, not
  // the screen's own sheet underneath it.
  const editorShown = !!model.activeEditor && model.activeEditor.objName === current?.obj_name
  const sheets: Sheet[] = [
    ...model.openScreens.map((item): Sheet => ({
      key: `s:${item.id}`, kind: 'screen', item, selected: current?.id === item.id && !editorShown,
    })),
    ...(model.editorsEnabled ? model.editors.map((session): Sheet => ({
      key: `e:${session.id}`, kind: 'editor', session,
      selected: editorShown && session.id === model.activeEditorId,
    })) : []),
  ]
  const [focusKey, setFocusKey] = useState<string | null>(null)
  const stop = (focusKey && sheets.some((s) => s.key === focusKey) ? focusKey : null)
    || sheets.find((s) => s.selected)?.key || sheets[0]?.key

  const pick = (s: Sheet) => {
    if (s.kind === 'editor') { model.restoreEditor(s.session.id); return }
    // The screen's own sheet while its editor is on display: back to the list.
    if (current?.id === s.item.id && editorShown) model.minimiseEditor()
    else model.open(s.item)
  }
  const closeSheet = (s: Sheet) => {
    if (s.kind === 'editor') model.closeEditor(s.session.id)
    else model.closeScreen(s.item)
  }
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')]
    if (moveFocus(els, e.key)) { e.preventDefault(); return }
    if (e.key === 'Delete') {
      const key = (document.activeElement as HTMLElement | null)?.dataset.sheet
      const s = sheets.find((x) => x.key === key)
      if (!s) return
      e.preventDefault()
      const strip = e.currentTarget
      const i = els.indexOf(document.activeElement as HTMLElement)
      closeSheet(s)
      // Keep the keyboard in the strip: the neighbour takes focus.
      setTimeout(() => {
        const left = [...strip.querySelectorAll<HTMLElement>('[role="tab"]')]
        left[Math.max(0, Math.min(i, left.length - 1))]?.focus()
      }, 0)
    }
  }

  const firstEditor = sheets.findIndex((s) => s.kind === 'editor')

  return (
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'stretch', height: 36, minWidth: 0,
      // Right padding: the Assistant's floating button sits over this corner.
      padding: '0 88px 0 8px', background: S.surface2, borderTop: `1px solid ${S.border}`,
    }}>
      <div role="tablist" aria-label="Open screens and editors" onKeyDown={onKey}
        {...tw('action', 'shell-office-sheets')}
        style={{ display: 'flex', alignItems: 'stretch', gap: 1, minWidth: 0, overflowX: 'auto' }}>
        {sheets.length === 0 && (
          <span style={{ alignSelf: 'center', padding: '0 8px', fontSize: 13, color: S.text2, whiteSpace: 'nowrap' }}>
            No screens open
          </span>
        )}
        {sheets.map((s, i) => {
          const on = s.selected
          const isEditor = s.kind === 'editor'
          const title = isEditor ? editorTitle(s.session) : s.item.title
          const screen = isEditor ? (model.itemFor(s.session.objName)?.title || s.session.objName) : ''
          const ariaLabel = isEditor
            ? `${s.session.mode === 'add' ? 'New' : 'Editing'} ${title}, ${screen}${s.session.dirty ? ', unsaved changes' : ''}`
            : title
          return (
            <div key={s.key} style={{ display: 'flex', alignItems: 'stretch' }}>
              {i === firstEditor && (
                <span aria-hidden style={{ alignSelf: 'center', width: 1, height: 20, margin: '0 6px', background: S.borderStrong }} />
              )}
              <div className="tw-oc-sheet" data-selected={on ? 'true' : 'false'} style={{
                display: 'flex', alignItems: 'center', maxWidth: 240, minWidth: 0,
                background: on ? S.panel : 'transparent',
                borderBottom: `3px solid ${on ? S.accent : 'transparent'}`,
                borderTop: '3px solid transparent',
              }}>
                <button type="button" role="tab" data-sheet={s.key} className="tw-oc-sheet-tab"
                  aria-selected={on} aria-controls={controls} aria-label={ariaLabel}
                  aria-keyshortcuts="Delete"
                  title={isEditor ? `${screen} — ${title}` : model.pathText(s.item) || title}
                  tabIndex={stop === s.key ? 0 : -1}
                  onFocus={() => setFocusKey(s.key)}
                  onClick={() => pick(s)}
                  {...(isEditor
                    ? tw('action', 'shell-office-editor-sheet', {
                      obj: s.session.objName, mode: s.session.mode, txn: s.session.txnId, dirty: s.session.dirty, selected: on,
                    })
                    : tw('action', 'shell-office-sheet', { obj: s.item.obj_name, type: s.item.obj_type, selected: on }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, height: '100%',
                    padding: '0 6px 0 12px', border: 'none', background: 'transparent', cursor: 'pointer',
                    color: on ? S.accent : S.text, fontFamily: 'inherit', fontSize: 14, fontWeight: on ? 600 : 400,
                    fontStyle: isEditor ? 'italic' : 'normal',
                  }}>
                  {isEditor
                    ? (s.session.mode === 'add' ? <Plus size={14} strokeWidth={2.4} aria-hidden /> : <Pencil size={13} strokeWidth={2.2} aria-hidden />)
                    : <NavIcon node={s.item} size={15} />}
                  <span className="tw-sh-clip">{title}</span>
                  {isEditor && s.session.dirty && <UnsavedDot />}
                </button>
                <button type="button" className="tw-sh-btn" tabIndex={-1}
                  onClick={() => closeSheet(s)}
                  aria-label={`Close ${title}`} title="Close"
                  {...(isEditor
                    ? tw('action', 'shell-office-editor-sheet-close', { obj: s.session.objName, txn: s.session.txnId })
                    : tw('action', 'shell-office-sheet-close', { obj: s.item.obj_name }))}
                  style={{ ...iconButton(26), marginRight: 4, borderRadius: 4 }}>
                  <X size={13} aria-hidden />
                </button>
              </div>
            </div>
          )
        })}
      </div>
      <IconTip label="Open another screen" place="above">
        <button type="button" className="tw-sh-btn" onClick={(e) => onNew(e.currentTarget)}
          aria-label="Open another screen" aria-haspopup="dialog"
          {...tw('action', 'shell-office-sheet-new')}
          style={{ ...iconButton(36), height: '100%', borderRadius: 0 }}>
          <CirclePlus size={17} aria-hidden />
        </button>
      </IconTip>
    </div>
  )
}
